import { useState, useEffect, useMemo } from "react";
import { Container, Grid2 as Grid, Typography, Stack, Box, ButtonBase, useTheme } from "@mui/material";
import { Content, Menu } from "@/types";
import DishTemplate from "@/components/DishTemplate";
import { useAppSelector, useResponsive } from "@/hooks";
import "./CatalogReader.scss";

interface CatalogReaderProps {
  menu: Menu;
  content: Content[];
  forceMobile?: boolean;
}

const CatalogReader = ({ menu, content, forceMobile = false }: CatalogReaderProps) => {
  const theme = useTheme();
  const { isMobile } = useResponsive();
  const structure = menu.structure || [];
  const activeBranch = useAppSelector((store) => store.ui.activeBranch);

  const showChipsBar = !isMobile && !forceMobile;

  // Calculate categories with visible products count
  const categoriesWithCounts = useMemo(() => {
    return structure
      .filter((section) => section.isVisible !== false)
      .map((section) => {
        const sectionProducts = (section.products ?? [])
          .map((id) => content.find((c) => c.id === id))
          .filter(Boolean) as Content[];

        const visibleProducts = sectionProducts.filter((product) => {
          const isDisabled = Boolean(product?.isDisabled) || Boolean(activeBranch && product?.branchSettings?.[activeBranch]?.isDisabled);
          return !isDisabled;
        });

        return {
          id: section.category,
          label: section.categoryTitle || "Categoría sin título",
          slug: section.category,
          count: visibleProducts.length,
        };
      })
      .filter((cat) => cat.count > 0);
  }, [structure, content, activeBranch]);

  const [activeCategory, setActiveCategory] = useState<string>("");

  // Track active section on scroll
  useEffect(() => {
    if (!showChipsBar || categoriesWithCounts.length === 0) return;

    const observerOptions = {
      root: null,
      rootMargin: "-120px 0px -60% 0px",
      threshold: 0,
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const sectionId = entry.target.id.replace("section-", "");
          setActiveCategory(sectionId);
        }
      });
    }, observerOptions);

    categoriesWithCounts.forEach((cat) => {
      const el = document.getElementById(`section-${cat.slug}`);
      if (el) observer.observe(el);
    });

    if (!activeCategory && categoriesWithCounts.length > 0) {
      setActiveCategory(categoriesWithCounts[0].slug);
    }

    return () => {
      observer.disconnect();
    };
  }, [categoriesWithCounts, showChipsBar, activeCategory]);

  const scrollToSection = (slug: string) => {
    const element = document.getElementById(`section-${slug}`);
    if (element) {
      // Offset for sticky chips bar (top: 0) and spacing
      const offset = 70;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      const offsetPosition = elementPosition - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
      setActiveCategory(slug);
    }
  };

  return (
    <Box
      sx={{
        width: "100% !important",
      }}
    >
      {/* Category Chips Bar: Desktop only */}
      {showChipsBar && categoriesWithCounts.length > 0 && (
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 10,
            backgroundColor: "#ffffff",
            py: 2,
            borderBottom: `1px solid ${theme.palette.divider}`,
            display: "flex",
            gap: 1.5,
            overflowX: "auto",
            px: 3,
          }}
        >
          <Box
            sx={{
              display: "flex",
              gap: 1.5,
              mx: "auto",
              width: "100%",
              maxWidth: "lg",
            }}
          >
            {categoriesWithCounts.map((cat) => {
              const isActive = activeCategory === cat.slug;
              return (
                <ButtonBase
                  key={cat.slug}
                  onClick={() => scrollToSection(cat.slug)}
                  sx={{
                    py: 1,
                    px: 2.5,
                    borderRadius: "50px",
                    backgroundColor: isActive ? theme.palette.primary.main : "#ffffff",
                    color: isActive ? "#ffffff" : theme.palette.text.primary,
                    border: `1px solid ${isActive ? theme.palette.primary.main : theme.palette.divider}`,
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    whiteSpace: "nowrap",
                    fontWeight: "600",
                    fontSize: "0.875rem",
                    transition: "all 0.2s ease-in-out",
                    "&:hover": {
                      backgroundColor: isActive ? theme.palette.primary.dark : theme.palette.action.hover,
                      borderColor: isActive ? theme.palette.primary.dark : theme.palette.divider,
                    },
                  }}
                >
                  {cat.label}
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 22,
                      height: 22,
                      borderRadius: "50%",
                      backgroundColor: isActive ? "rgba(255, 255, 255, 0.25)" : "rgba(0, 0, 0, 0.08)",
                      color: isActive ? "#ffffff" : theme.palette.text.secondary,
                      fontSize: "0.75rem",
                      fontWeight: "700",
                    }}
                  >
                    {cat.count}
                  </Box>
                </ButtonBase>
              );
            })}
          </Box>
        </Box>
      )}

      <Container maxWidth="lg" sx={{ py: 4, px: 2, pb: 12 }}>
        <Stack spacing={3}>
          {structure
            .filter((section) => section.isVisible !== false)
            .map((section, idx) => {
              // Find products in this section
              const sectionProducts = (section.products ?? [])
                .map((id) => content.find((c) => c.id === id))
                .filter(Boolean) as Content[];

              // Filter out disabled products (taking branch vs global settings into account)
              const visibleProducts = sectionProducts.filter((product) => {
                const isDisabled = Boolean(product?.isDisabled) || Boolean(activeBranch && product?.branchSettings?.[activeBranch]?.isDisabled);
                return !isDisabled;
              });

              if (visibleProducts.length === 0) return null;

              // Determine columns structure
              const cols = Number(section.columns) || 1;
              const gridSpacing = 2;

              return (
                <Stack
                  spacing={2}
                  key={section.category || idx}
                  id={`section-${section.category}`}
                  className="dish-container"
                >
                  {/* Section Header */}
                  <Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontSize: forceMobile ? "1.25rem" : { xs: "1.25rem", sm: "1.5rem" },
                      }}
                    >
                      {section.categoryTitle || "Categoría sin título"}
                    </Typography>
                  </Box>

                  {/* Products Grid */}
                  <Grid container spacing={gridSpacing}>
                    {visibleProducts.map((product) => (
                      <Grid
                        key={product.id}
                        size={
                          forceMobile
                            ? 12
                            : {
                              xs: 12,
                              sm: cols > 1 ? 6 : 12,
                              md: cols === 4 ? 3 : cols === 3 ? 4 : cols === 2 ? 6 : 12,
                            }
                        }
                      >
                        <DishTemplate
                          dish={product}
                          template={section.template || "default-filled"}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Stack>
              );
            })}

          {structure.length === 0 && (
            <Box sx={{ py: 8, textAlign: "center" }}>
              <Typography variant="body1" color="text.secondary">
                Este menú aún no tiene productos o secciones cargadas.
              </Typography>
            </Box>
          )}
        </Stack>
      </Container>
    </Box>
  );
};

export default CatalogReader;
